import React from 'react'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
} from '@mui/material'
import { AccessTime } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { calculatePrice } from '../utils/pricing'

const Services = ({ companyData }) => {
  const navigate = useNavigate()
  const { jobTypes, company } = companyData || {}

  const formatDuration = duration => {
    if (!duration) return 'N/A'
    const parts = duration.split(':')
    const hours = parseInt(parts[0])
    const minutes = parseInt(parts[1])

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`
    } else if (minutes > 0) {
      return `${minutes} minutes`
    }
    return duration
  }

  return (
    <Container maxWidth='lg' sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ mb: 8 }}>
        <Typography variant='h3' gutterBottom color='primary.main' sx={{ mb: 2 }}>
          Our Services
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ fontSize: '1.1rem' }}>
          Choose the service you need, get booked on and save money.
        </Typography>
      </Box>

      {jobTypes && jobTypes.length > 0 ? (
        <Grid container spacing={4}>
          {jobTypes.map(jobType => (
            <Grid item xs={12} sm={6} md={4} key={jobType.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate('/', {
                    state: {
                      searchJobType: jobType.name,
                      searchJobTypeId: jobType.id,
                    },
                  })}
                  sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start' }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant='h5' gutterBottom color='primary'>
                      {jobType.name}
                    </Typography>

                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant='h6'
                          sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                        >
                          £
                        </Typography>
                        <Typography variant='body1'>
                          <strong>Starting from:</strong> £
                          {calculatePrice(jobType.startingPrice, company).toFixed(2)}
                          {company?.chargeVat && (
                            <Typography
                              component='span'
                              variant='body2'
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              inc VAT
                            </Typography>
                          )}
                        </Typography>
                      </Box>

                      {jobType.minimumBookingPrice && Number(jobType.minimumBookingPrice) > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant='h6'
                            sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                          >
                            £
                          </Typography>
                          <Typography variant='body1'>
                            <strong>Lowest group booking price:</strong> £
                            {calculatePrice(jobType.minimumBookingPrice, company).toFixed(2)}
                            {company?.chargeVat && (
                              <Typography
                                component='span'
                                variant='body2'
                                sx={{ ml: 1, color: 'text.secondary' }}
                              >
                                inc VAT
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      )}

                      {jobType.durationMinutes && (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <AccessTime sx={{ color: 'text.secondary' }} />
                          <Typography variant='body1'>
                            <strong>Duration:</strong>{' '}
                            {formatDuration(jobType.durationMinutes)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Chip
                      label='Group Discount Available'
                      sx={{
                        mt: 1,
                        bgcolor: '#48eca2',
                        color: '#201e31',
                        fontWeight: 600,
                      }}
                      size='small'
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant='h6' color='text.secondary'>
            No services available at the moment
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          mt: 8,
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='h5' gutterBottom sx={{ mb: 2 }}>
          How group bookings work
        </Typography>
        <Typography variant='body1'>
          Our smart tech groups you with nearby neighbours, cutting travel
          time for our engineers and passes the savings straight back to you.
          The more people on the day, the less you pay.
        </Typography>
      </Box>
    </Container>
  )
}

export default Services
